import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, AppRole } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { buildLoginRedirect } from '@/lib/auth/redirect-utils';
import { AUTH_ROUTES, ERROR_ROUTES } from '@/lib/auth/constants';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  requireActive?: boolean;
  requireInternal?: boolean;
  requireMFA?: boolean;
}

// Hook to check MFA status
function useMFAStatus() {
  const [mfaVerified, setMfaVerified] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      setIsChecking(false);
      return;
    }

    const checkMFA = async () => {
      try {
        // Get current session AAL (Authenticator Assurance Level)
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (error) {
          console.error('MFA check error:', error);
          setMfaVerified(false);
        } else {
          // currentLevel === 'aal2' means MFA completed this session
          // nextLevel === 'aal1' means user has NO MFA factors enrolled (MFA not required)
          // If user has factors enrolled (nextLevel === 'aal2') but currentLevel is 'aal1',
          // they need to complete MFA verification
          const isMfaComplete = data.currentLevel === 'aal2';
          const noMfaRequired = data.nextLevel === 'aal1';
          setMfaVerified(isMfaComplete || noMfaRequired);
        }
      } catch (err) {
        console.error('MFA status check failed:', err);
        setMfaVerified(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkMFA();
  }, [isAuthenticated]);

  return { mfaVerified, isChecking };
}

export const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  requireActive = true,
  requireInternal = false,
  requireMFA = false
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, role, profile, authInfo, isInternalStaff, isInternalManager } = useAuth();
  const location = useLocation();
  const { mfaVerified, isChecking: mfaChecking } = useMFAStatus();

  // Show loading state while checking auth
  if (isLoading || (requireMFA && mfaChecking)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login with returnTo
  if (!isAuthenticated) {
    const loginUrl = buildLoginRedirect(location.pathname);
    return <Navigate to={loginUrl} replace />;
  }

  // Check if account is active
  if (requireActive && profile && !profile.is_active) {
    return <Navigate to={ERROR_ROUTES.ACCOUNT_DISABLED} replace />;
  }

  // Check if internal staff is required
  if (requireInternal && !isInternalStaff) {
    return <Navigate to={ERROR_ROUTES.UNAUTHORIZED} replace />;
  }

  // Check role permissions
  // Internal MANAGERS (not agents) can bypass role checks for admin routes
  // Internal AGENTS cannot access admin - they go to agent portal only
  if (allowedRoles && role) {
    // Check if this is an admin route
    const isAdminRoute = location.pathname.startsWith('/admin');
    
    // For admin routes: only allow if role is in allowedRoles OR user is internal manager
    // For other routes: allow if role is in allowedRoles OR user is internal staff
    const hasAccess = allowedRoles.includes(role) || 
      (isAdminRoute ? isInternalManager : isInternalStaff);
    
    if (!hasAccess) {
      return <Navigate to={ERROR_ROUTES.UNAUTHORIZED} replace />;
    }
  }

  // Check MFA requirement - redirect to MFA page if not verified
  if (requireMFA && mfaVerified === false) {
    const mfaUrl = `${AUTH_ROUTES.MFA}?returnTo=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={mfaUrl} replace />;
  }

  return <>{children}</>;
}

// Convenience components for specific portal sections
export const ClientRoute = ({ children }: { children: React.ReactNode }) => {
  const { isInternalStaff, isLoading } = useAuth();
  
  // Internal staff should NOT access client portal - redirect to admin
  if (!isLoading && isInternalStaff) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return (
    <ProtectedRoute allowedRoles={['client']}>
      {children}
    </ProtectedRoute>
  );
};

export const AgentRoute = ({ children }: { children: React.ReactNode }) => {
  // Agents, managers, admins, and internal staff can access
  return (
    <ProtectedRoute allowedRoles={['agent', 'manager', 'admin']}>
      {children}
    </ProtectedRoute>
  );
};

export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  // Managers, admins, and internal staff can access
  return (
    <ProtectedRoute allowedRoles={['manager', 'admin']}>
      {children}
    </ProtectedRoute>
  );
};

export const SecureAdminRoute = ({ children }: { children: React.ReactNode }) => {
  // Admin routes that require MFA (sensitive operations)
  return (
    <ProtectedRoute allowedRoles={['manager', 'admin']} requireMFA>
      {children}
    </ProtectedRoute>
  );
};

export const InternalRoute = ({ children }: { children: React.ReactNode }) => {
  // Only internal staff (super_admin, etc.) can access
  return (
    <ProtectedRoute requireInternal>
      {children}
    </ProtectedRoute>
  );
};
